import type { DocumentTab } from 'features/knowledgeDocumentViewer/types'
import {
  getDocChatStorageOwner,
  isCurrentDocChatStorageOwner,
} from 'features/knowledgeDocumentViewer/utils/docChatStorageOwner'
import { z } from 'zod'

import { resolveAgenticConversationIdentity } from './agenticConversationIdentity'

const PREFIX = 'gecko_docpage_chat_composer_drafts'
const VERSION = 1

const draftsPayloadSchema = z.object({
  _v: z.literal(VERSION),
  drafts: z.record(z.string(), z.string()),
})

function storageKey(owner: string | null, siteId: string): string | null {
  return isCurrentDocChatStorageOwner(owner)
    ? `${PREFIX}_${owner}_${siteId}`
    : null
}

function readDrafts(
  owner: string | null,
  siteId: string
): Record<string, string> {
  const key = storageKey(owner, siteId)
  if (!key) return {}
  try {
    const raw = sessionStorage.getItem(key)
    if (!raw) return {}
    const result = draftsPayloadSchema.safeParse(JSON.parse(raw))
    if (!result.success) return {}
    return result.data.drafts
  } catch {
    return {}
  }
}

function writeDrafts(
  owner: string | null,
  siteId: string,
  drafts: Record<string, string>
): void {
  const key = storageKey(owner, siteId)
  if (!key) return
  try {
    if (Object.keys(drafts).length === 0) {
      sessionStorage.removeItem(key)
      return
    }
    sessionStorage.setItem(key, JSON.stringify({ _v: VERSION, drafts }))
  } catch (e) {
    if (e instanceof DOMException && e.name === 'QuotaExceededError') {
      try {
        sessionStorage.removeItem(key)
      } catch {
        /* noop */
      }
    }
  }
}

export function getAgenticChatComposerDraft(
  tab: DocumentTab
): string | undefined {
  const item = tab.history[tab.currentIndex]
  if (item?.type !== 'search') return undefined
  return item.searchState.inputQuery
}

/**
 * Submitted chats store an empty draft (`''`). That must win over a stale
 * sessionStorage value so reopen does not resurrect the last sent query.
 */
export function resolveAgenticChatComposerDraft({
  sessionDraft,
  storedDraft,
}: {
  sessionDraft?: string
  storedDraft?: string
}): string {
  if (sessionDraft !== undefined) return sessionDraft
  return storedDraft ?? ''
}

export function readAgenticChatComposerDraft(
  siteId: string,
  sessionKey: string
): string | undefined {
  const draft = readDrafts(getDocChatStorageOwner(), siteId)[sessionKey]
  return draft === undefined || draft === '' ? undefined : draft
}

export function writeAgenticChatComposerDraft(
  siteId: string,
  sessionKey: string,
  draft: string
): void {
  writeDraftFor(getDocChatStorageOwner(), siteId, sessionKey, draft)
}

function writeDraftFor(
  owner: string | null,
  siteId: string,
  sessionKey: string,
  draft: string
): void {
  const drafts = readDrafts(owner, siteId)
  if (draft === '') {
    if (!(sessionKey in drafts)) return
    const next = { ...drafts }
    delete next[sessionKey]
    writeDrafts(owner, siteId, next)
    return
  }
  if (drafts[sessionKey] === draft) return
  writeDrafts(owner, siteId, { ...drafts, [sessionKey]: draft })
}

export function clearAgenticChatComposerDraft(
  siteId: string,
  sessionKey: string
): void {
  writeAgenticChatComposerDraft(siteId, sessionKey, '')
}

/** Pending chats key by tab; also write conversation.id so reopen after id assignment still finds the draft. */
function composerDraftStorageKeys({
  conversationId,
  sessionKey,
}: {
  conversationId?: string
  sessionKey: string
}): string[] {
  if (conversationId === undefined || conversationId === sessionKey) {
    return [sessionKey]
  }
  return [sessionKey, conversationId]
}

export function readStoredComposerDraftForSession({
  conversationId,
  sessionKey,
  siteId,
}: {
  conversationId?: string
  sessionKey: string
  siteId: string
}): string | undefined {
  for (const key of composerDraftStorageKeys({ conversationId, sessionKey })) {
    const draft = readAgenticChatComposerDraft(siteId, key)
    if (draft !== undefined) return draft
  }
  return undefined
}

export function persistAgenticChatComposerDrafts(
  owner: string | null,
  sessions: Array<{
    composerDraft?: string
    conversation: { id?: string }
    key: string
    siteId: string
  }>
): void {
  sessions.forEach((session) => {
    if (session.composerDraft === undefined) return
    const composerDraft = session.composerDraft
    composerDraftStorageKeys({
      conversationId: session.conversation.id,
      sessionKey: session.key,
    }).forEach((key) => {
      writeDraftFor(owner, session.siteId, key, composerDraft)
    })
  })
}

export function clearAgenticChatComposerDraftsForSession({
  conversationId,
  sessionKey,
  siteId,
}: {
  conversationId?: string
  sessionKey: string
  siteId: string
}): void {
  composerDraftStorageKeys({ conversationId, sessionKey }).forEach((key) => {
    clearAgenticChatComposerDraft(siteId, key)
  })
}

/**
 * Reload can snapshot `inputQuery: ''` while a turn streams (tab persist is
 * debounced and reset by stream patches). Fill those empty composers from
 * sessionStorage. Do not use `resolveAgenticChatComposerDraft`: in-memory `''`
 * means submitted/cleared, which a stale persisted tab cannot tell apart.
 */
export function applyStoredComposerDraftsToOpenTabs(
  openTabs: DocumentTab[]
): DocumentTab[] {
  let changed = false
  const nextTabs = openTabs.map((tab) => {
    const identity = resolveAgenticConversationIdentity(tab)
    if (!identity) return tab
    const item = tab.history[tab.currentIndex]
    if (item.type !== 'search') return tab
    if (item.searchState.inputQuery) return tab
    const siteId = item.searchState.docChat?.siteId
    if (!siteId) return tab
    const stored = readStoredComposerDraftForSession({
      conversationId: identity.conversation.id,
      sessionKey: identity.key,
      siteId,
    })
    if (stored === undefined) return tab
    changed = true
    const nextHistory = [...tab.history]
    nextHistory[tab.currentIndex] = {
      ...item,
      searchState: {
        ...item.searchState,
        inputQuery: stored,
      },
    }
    return { ...tab, history: nextHistory }
  })
  return changed ? nextTabs : openTabs
}
